/**
 * Modal de criação de playbook (Brief 4 · Tela 1 · PR5 · gate `playbook-author`).
 *
 * Form mínimo (nome · vertical · objetivo · critério de sucesso) com defaults
 * seguros no backend para o resto. O playbook nasce `active` (o POST não aceita
 * `draft` · R15 ao vivo) — a copy diz isso explicitamente (zero dado falso). Ao
 * criar, o chamador navega para o editor do novo playbook (PR4) para completar
 * etapas/modos. Renderiza dentro do `.playbooks-screen` para herdar os estilos
 * `pb-*` escopados (o Modal da DS é inline, não portal).
 */

import { Modal } from '@lume/ui-components';
import { useEffect, useState } from 'react';

import { ApiError } from '../../lib/api-client';

import { buildCreatePayload, CREATE_VERTICALS, validateCreateForm } from './playbook-create';
import { verticalLabel } from './playbook-labels';
import { useCreatePlaybook } from './use-playbooks';

import type { CreateFormErrors } from './playbook-create';
import type { PlaybookDto, PlaybookVertical } from './types';

export function PlaybookCreateModal({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (playbook: PlaybookDto) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [vertical, setVertical] = useState<PlaybookVertical>('support');
  const [goal, setGoal] = useState('');
  const [successCriteria, setSuccessCriteria] = useState('');
  const [errors, setErrors] = useState<CreateFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const mutation = useCreatePlaybook();

  // Reseta o form quando o modal fecha (o componente fica montado; sem isto a
  // próxima abertura traria os campos da criação anterior).
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setVertical('support');
      setGoal('');
      setSuccessCriteria('');
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen]);

  const handleConfirm = (): void => {
    if (mutation.isPending) return;
    const fieldErrors = validateCreateForm({ name, goal, successCriteria });
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;
    setSubmitError(null);
    mutation.mutate(buildCreatePayload({ name, vertical, goal, successCriteria }), {
      onSuccess: (created) => {
        onCreated(created);
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          setSubmitError('Já existe um playbook com esse nome. Escolha outro nome.');
          return;
        }
        setSubmitError('Não foi possível criar o playbook. Tente novamente.');
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Novo playbook"
      confirmLabel={mutation.isPending ? 'Criando…' : 'Criar playbook'}
      cancelLabel="Cancelar"
      onConfirm={handleConfirm}
    >
      <div className="pb-create-form">
        <div className="pb-field">
          <label className="pb-field__label" htmlFor="pb-create-name">
            Nome
          </label>
          <input
            id="pb-create-name"
            className="pb-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex: Diagnóstico de instabilidade"
            maxLength={120}
          />
          {errors.name !== undefined && (
            <span className="pb-field__error" role="alert">
              {errors.name}
            </span>
          )}
        </div>

        <div className="pb-field">
          <label className="pb-field__label" htmlFor="pb-create-vertical">
            Vertical
          </label>
          <select
            id="pb-create-vertical"
            className="pb-select"
            value={vertical}
            onChange={(e) => setVertical(e.target.value as PlaybookVertical)}
          >
            {CREATE_VERTICALS.map((v) => (
              <option key={v} value={v}>
                {verticalLabel(v)}
              </option>
            ))}
          </select>
        </div>

        <div className="pb-field">
          <label className="pb-field__label" htmlFor="pb-create-goal">
            Objetivo
          </label>
          <textarea
            id="pb-create-goal"
            className="pb-textarea"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            rows={2}
            placeholder="O que este playbook deve alcançar?"
            maxLength={10000}
          />
          {errors.goal !== undefined && (
            <span className="pb-field__error" role="alert">
              {errors.goal}
            </span>
          )}
        </div>

        <div className="pb-field">
          <label className="pb-field__label" htmlFor="pb-create-criteria">
            Critério de sucesso
          </label>
          <textarea
            id="pb-create-criteria"
            className="pb-textarea"
            value={successCriteria}
            onChange={(e) => setSuccessCriteria(e.target.value)}
            rows={2}
            placeholder="Como saber que o atendimento foi bem-sucedido?"
            maxLength={500}
          />
          {errors.successCriteria !== undefined && (
            <span className="pb-field__error" role="alert">
              {errors.successCriteria}
            </span>
          )}
        </div>

        <p className="pb-create-form__note">
          O playbook será criado como <strong>Ativo</strong>. Você poderá ajustar etapas, modos de
          operação e demais campos no editor logo em seguida.
        </p>

        {submitError !== null && (
          <p className="pb-alert" role="alert">
            {submitError}
          </p>
        )}
      </div>
    </Modal>
  );
}
